"use client";

import { createContext, useContext } from "react";
import { useTheme } from "next-themes";
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner";

// sonner is an implementation detail — feature code imports `useToast` from here, never "sonner".
//
// The store sonner uses is a module-level singleton. In the App Router, separate "use client"
// entry points (e.g. the root layout vs. a feature in the auth layout) can each evaluate the
// sonner module in their own chunk, producing two stores: a `toast()` call writes to one while
// <Toaster> reads the other, so the toast silently never renders. Delivering the same `toast`
// reference through React context — from the one provider that also renders <Toaster> — sidesteps
// that: context flows across client-chunk boundaries, module singletons do not.

type ToastFn = typeof sonnerToast;

const ToastContext = createContext<ToastFn | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { theme = "system" } = useTheme();

  return (
    <ToastContext.Provider value={sonnerToast}>
      <SonnerToaster
        theme={theme as React.ComponentProps<typeof SonnerToaster>["theme"]}
        className="toaster group"
        closeButton
        toastOptions={{
          classNames: {
            // Elevated popover surface (not bg-background) so the toast reads as a floating
            // panel on the dark UI; a 4px left border carries severity color (set per type).
            toast:
              "group toast group-[.toaster]:bg-popover group-[.toaster]:text-popover-foreground group-[.toaster]:border-border group-[.toaster]:border-l-4 group-[.toaster]:shadow-xl",
            description: "group-[.toast]:text-muted-foreground",
            actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
            cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
            closeButton:
              "group-[.toast]:bg-popover group-[.toast]:border-border group-[.toast]:text-muted-foreground",
            warning: "!border-l-warning",
            error: "!border-l-destructive",
            success: "!border-l-success",
            info: "!border-l-primary",
          },
        }}
      />
      {children}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastFn {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error("useToast must be used within <ToastProvider>");
  return toast;
}
